<template>
  <div class="card events-card">
    <div class="header-dark d-flex justify-content-between align-items-center">
      <div>{{ $t('sessionsInfo.events') }}</div>
      <svg-icon
        :name="'filter'"
        class="svg-icon"
        @click:icon="openEventFilter"
        :height="27"
        :width="27"
      ></svg-icon>
      <div v-if="isFilterSet" class="filter-exists"></div>
    </div>
    <div class="event-container">
      <div class="overflow-auto" v-if="filteredEvents && filteredEvents.length > 0">
        <div
          class="mb-2 event-element text-nowrap overflow-hidden d-block"
          v-for="(ev, id) in filteredEvents"
          :key="id"
          style="font-size: 14px;"
        >
          <div class="d-flex">
            <span
              class="badge badge-fix-s badge-pill mr-2"
              :class="ev.color ? 'badge-' + ev.color : 'badge-danger'"
              v-html="ev.abbr"
            >
            </span>
            <div
              style="
              text-overflow: ellipsis; 
              overflow: hidden;"
            >
              <span class="mr-1">
                {{ ev.time }}
              </span>
              <span>
                {{ ev.patientName }}
              </span>
            </div>
          </div>

          <div v-if="ev.value" class="font-weight-bolder text-truncate" style="margin-left:54px;">
            {{ ev.value }} <span> {{ ev.unit }}</span>
          </div>
        </div>
      </div>
    </div>

    <base-modal
      :is-show="isShowModal"
      class="filter-modal"
      size="sm"
      mode="apply"
      @close="closeModal"
      @cancel="closeModal"
      @confirm="confirmChanges"
    >
      <template #header>{{ $t('general.filterIncomingEvents') }}</template>
      <template #body>
        <div>
          <cardio-events-filter
            :noFilterSpecified="noFilterSpecified"
            :selected-events="selectedEvents"
            :selected-importance="selectedImportance"
            @set:events="inputEvents"
            @input:importance="inputImportance"
            class="filter-multiple-column"
          ></cardio-events-filter>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<script>
import { subscribeMobileEventsMultiple } from '@/mixins/subscribe-mobile-events-multiple.js'
import { eventTypes } from '@/main.config.js'
import timeString from '@/modules/time-string.js'
import { eventsConfig } from '@/events.config.js'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'IncomingEventsList',
  components: {
    BaseModal: () => import('@/base/BaseModal.vue'),
    CardioEventsFilter: () => import('@/components/Filters/CardioEventsFilterInMultitraining.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue')
  },
  mixins: [subscribeMobileEventsMultiple],
  props: {
    trainingsWithPatientName: {
      type: Array,
      required: true
    },
    events: {
      type: Array,
      required: true
    }
  },
  watch: {
    events: {
      handler(newEvents) {
        if (newEvents) {
          this.allEvents = newEvents.slice()
        }
      },
      immediate: true
    }
  },
  data: () => ({
    allEvents: [],
    isShowModal: false,
    selectedImportance: ['danger', 'warning', 'primary', 'secondary'],
    selectedImportanceInSettings: ['danger', 'warning', 'primary', 'secondary'],
    selectedEvents: [],
    selectedEventsinSettings: [],
    noFilterSpecified: true
  }),
  created() {
    this.canSubscribe = true
    if (this.events) {
      this.allEvents = this.events.slice()
    }
    let filterIncomingEvents = JSON.parse(localStorage.getItem('filterIncomingEvents'))
    if (filterIncomingEvents) {
      this.noFilterSpecified = false
      this.selectedEvents = filterIncomingEvents.events
      this.selectedImportance = filterIncomingEvents.importance
    } else {
      this.selectedEvents = this.addEventsToList()
      this.saveSelectedEventsInStorage()
    }

    this.selectedEventsinSettings = cloneDeep(this.selectedEvents)
    this.selectedImportanceInSettings = cloneDeep(this.selectedImportance)
  },
  computed: {
    isFilterSet() {
      return this.selectedEvents && this.selectedEvents.length == 28 ? false : true
    },
    filteredEvents() {
      return this.allEvents.filter(event => this.selectedEvents.includes(event.episodeType))
    }
  },
  methods: {
    addEventsToList() {
      let res = []
      Object.keys(eventsConfig).forEach(key => {
        if (key !== 'break' && key !== 'reconnected') {
          const event = eventsConfig[key]
          event.type = key
          res.push(event)
        }
      })
      const sortArray = this.sortArray(res)
      return sortArray
    },
    sortArray(array) {
      let danger = [],
        warning = [],
        primary = [],
        secondary = []

      array.forEach(element => {
        if (this.selectedImportance.indexOf(element.color) !== -1) {
          switch (element.color) {
            case 'warning':
              warning.push(element)
              break
            case 'primary':
              primary.push(element)
              break
            case 'secondary':
              secondary.push(element)
              break
            default:
              danger.push(element)
              break
          }
        }
      }, this)

      let sorted = []
      sorted.push(...danger, ...warning, ...secondary, ...primary)
      return sorted
    },
    openEventFilter() {
      this.isShowModal = true
    },
    closeModal() {
      this.isShowModal = false
    },
    confirmChanges() {
      this.selectedEvents = this.selectedEventsinSettings
      this.selectedImportance = this.selectedImportanceInSettings
      this.saveSelectedEventsInStorage()
      this.closeModal()
    },
    saveSelectedEventsInStorage() {
      localStorage.setItem(
        'filterIncomingEvents',
        JSON.stringify({ events: this.selectedEvents, importance: this.selectedImportance })
      )
    },
    changeDataByEvent(event) {
      if (event.type === eventTypes.cardioEvent) {
        this.addCardioEvent(event)
      }
    },
    addCardioEvent(event) {
      let time = timeString(Math.round(event.time), true)
      let eventObj = {
        isDuration: eventsConfig[event.episodeType].duration,
        episodeType: event.episodeType,
        text: eventsConfig[event.episodeType].text,
        value:
          event.episodeType === 'UserEvent'
            ? `${this.$t('sessionsInfo.heartRate')}: ${event.value}`
            : event.value,
        unit:
          event.episodeType === 'UserEvent' ? `${this.$t('unit.bpm')}` : eventsConfig[event.episodeType].unit,
        abbr: eventsConfig[event.episodeType].abbr,
        color: eventsConfig[event.episodeType].color,
        time: time,
        patientName: this.getPatientNameByTrainingId(event.tr_id)
      }
      this.allEvents.push(eventObj)
      if (this.selectedEvents.includes(event.episodeType)) {
        this.showNotification(eventObj)
      }
    },
    getPatientNameByTrainingId(trainingId) {
      const foundObject = this.trainingsWithPatientName.find(item => item.id === trainingId)
      if (foundObject) {
        return foundObject.patient_name
      } else {
        return 'Not found'
      }
    },
    inputEvents(selectedEvents) {
      this.selectedEventsinSettings = cloneDeep(selectedEvents)
    },
    inputImportance(selectedImportance) {
      this.selectedImportanceInSettings = cloneDeep(selectedImportance)
    },
    showNotification(event) {
      this.$emit('show:notification', event)
    }
  }
}
</script>
